import React, { useState, useEffect } from "react";
import {
  useProductQuery,
  useRemoveFeatureMutation,
} from "../../../../hooks/useAdminProductCatalog";
import type {
  ProductFeature,
  ProductStatus,
  EnrichedPackageStructure,
} from "../../../../types/adminBilling";
import {
  PRODUCT_STATUS_CLASSES,
  PRODUCT_STATUS_LABELS,
  PRODUCT_TYPE_CLASSES,
  PRODUCT_TYPE_LABELS,
} from "./constants";
import { FeatureForm } from "./FeatureForm";
import { RemoveFeatureConfirmModal } from "./RemoveFeatureConfirmModal";
import { PackageStructureViewer } from "./PackageStructureViewer";
import { PackageStructureEditor } from "./PackageStructureEditor";
import { ProductPricingPreview } from "./ProductPricingPreview";
import { PackagePricingConfigurator } from "./PackagePricingConfigurator";
import { ProductApplicabilityEditor } from "./ProductApplicabilityEditor";

export interface ProductDetailModalProps {
  productId: string;
  brandId: string;
  onClose: () => void;
  onEditProduct: () => void;
  onTransition: (target: ProductStatus) => void;
}

/**
 * Checks whether the given value looks like a valid PackageStructure
 * (has a `sets` array). Used to safely cast the generic Record type.
 */
const isPackageStructure = (value: unknown): value is EnrichedPackageStructure => {
  if (!value || typeof value !== "object") return false;
  return Array.isArray((value as EnrichedPackageStructure).sets);
};

export const ProductDetailModal: React.FC<ProductDetailModalProps> = ({
  productId,
  brandId,
  onClose,
  onEditProduct,
  onTransition,
}) => {
  const { data: product, isLoading, refetch } = useProductQuery(productId, brandId);
  const removeMutation = useRemoveFeatureMutation(productId, brandId);
  const [showFeatureForm, setShowFeatureForm] = useState(false);
  const [editingFeature, setEditingFeature] = useState<ProductFeature | null>(
    null
  );
  const [removingFeatureId, setRemovingFeatureId] = useState<string | null>(
    null
  );
  const [editingStructure, setEditingStructure] = useState(false);

  // Escape-to-close (cancel editing first if active)
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (editingStructure) {
          setEditingStructure(false);
        } else {
          onClose();
        }
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [onClose, editingStructure]);

  const handleRemoveFeatureConfirm = () => {
    if (!removingFeatureId) return;
    removeMutation.mutate(removingFeatureId, {
      onSuccess: () => setRemovingFeatureId(null),
    });
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      </div>
    );
  }

  if (!product) return null;

  const canTransitionToActive =
    product.status === "DRAFT";
  const canTransitionToArchived =
    product.status === "DRAFT" || product.status === "ACTIVE";
  const canAddFeatures = product.status !== "ARCHIVED";
  const isPackage = product.productType === "PACKAGE";
  const canEditStructure = isPackage && product.status === "DRAFT";

  // C4: Expand modal to max-w-3xl for PACKAGE products
  const modalMaxWidth = isPackage ? "max-w-3xl" : "max-w-2xl";

  const packageStructure = isPackageStructure(product.packageStructure)
    ? product.packageStructure
    : null;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className={`bg-white rounded-lg ${modalMaxWidth} w-full mx-4 max-h-[90vh] overflow-y-auto`}>
          <div className="px-6 py-4 border-b border-neutral-200 flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-neutral-900">
                {product.name}
              </h3>
              <div className="flex gap-2 mt-1">
                <span
                  className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                    PRODUCT_TYPE_CLASSES[product.productType]
                  }`}
                >
                  {PRODUCT_TYPE_LABELS[product.productType]}
                </span>
                <span
                  className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                    PRODUCT_STATUS_CLASSES[product.status as ProductStatus]
                  }`}
                >
                  {PRODUCT_STATUS_LABELS[product.status as ProductStatus]}
                </span>
              </div>
            </div>
            <button
              onClick={onClose}
              aria-label="Zamknij"
              className="text-neutral-400 hover:text-neutral-600"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <div className="p-6 space-y-6">
            {/* Actions */}
            <div className="flex flex-wrap gap-2">
              {product.status !== "ARCHIVED" && (
                <button
                  onClick={onEditProduct}
                  className="px-3 py-1.5 text-sm border border-neutral-300 rounded-md hover:bg-neutral-50 text-neutral-700 font-medium"
                >
                  Edytuj
                </button>
              )}
              {canTransitionToActive && (
                <button
                  onClick={() => onTransition("ACTIVE")}
                  className="px-3 py-1.5 text-sm border border-green-300 rounded-md hover:bg-green-50 text-green-700 font-medium"
                >
                  Aktywuj
                </button>
              )}
              {canTransitionToArchived && (
                <button
                  onClick={() => onTransition("ARCHIVED")}
                  className="px-3 py-1.5 text-sm border border-red-300 rounded-md hover:bg-red-50 text-red-700 font-medium"
                >
                  Zarchiwizuj
                </button>
              )}
            </div>

            {/* Info */}
            {product.description && (
              <div>
                <span className="text-sm font-medium text-neutral-700">
                  Opis:
                </span>
                <p className="text-sm text-neutral-600 mt-1">
                  {product.description}
                </p>
              </div>
            )}

            {/* Metadata */}
            {product.metadata && Object.keys(product.metadata).length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-neutral-900 mb-2">Metadata</h4>
                <pre className="text-xs text-neutral-600 bg-neutral-50 p-3 rounded-md border border-neutral-200 overflow-x-auto">
                  {JSON.stringify(product.metadata, null, 2)}
                </pre>
              </div>
            )}

            {/* AC-13: Applicability + validTo editor */}
            <ProductApplicabilityEditor
              product={product}
              brandId={brandId}
              onSaved={() => void refetch()}
            />

            {/* Features */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-semibold text-neutral-900">
                  Features ({product.features?.length ?? 0})
                </h4>
                {canAddFeatures && !showFeatureForm && (
                  <button
                    onClick={() => {
                      setEditingFeature(null);
                      setShowFeatureForm(true);
                    }}
                    className="text-xs text-primary-600 hover:text-primary-800 font-medium"
                  >
                    + Dodaj feature
                  </button>
                )}
              </div>

              {showFeatureForm && (
                <FeatureForm
                  productId={productId}
                  brandId={brandId}
                  feature={editingFeature}
                  onClose={() => {
                    setShowFeatureForm(false);
                    setEditingFeature(null);
                  }}
                />
              )}

              {product.features && product.features.length > 0 ? (
                <div className="space-y-2">
                  {product.features.map((f) => (
                    <div
                      key={f.id}
                      className="flex items-start justify-between p-3 bg-neutral-50 rounded-md border border-neutral-200"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <code className="text-xs font-mono text-neutral-800 bg-white px-1.5 py-0.5 rounded border border-neutral-200">
                            {f.featureCode}
                          </code>
                          <span className="text-xs text-neutral-500">
                            {f.constraintType}
                          </span>
                          {f.isMandatory && (
                            <span className="text-xs bg-primary-100 text-primary-800 px-1.5 py-0.5 rounded">
                              Obowiązkowy
                            </span>
                          )}
                        </div>
                        <code className="text-xs text-neutral-500 mt-1 block truncate">
                          {JSON.stringify(f.constraintConfig)}
                        </code>
                      </div>
                      {canAddFeatures && (
                        <div className="flex gap-1 ml-2 flex-shrink-0">
                          <button
                            onClick={() => {
                              setEditingFeature(f);
                              setShowFeatureForm(true);
                            }}
                            className="text-xs text-primary-600 hover:text-primary-800 px-2 py-1"
                          >
                            Edytuj
                          </button>
                          <button
                            onClick={() => setRemovingFeatureId(f.id)}
                            disabled={removeMutation.isPending}
                            className="text-xs text-red-600 hover:text-red-800 px-2 py-1 disabled:opacity-50"
                          >
                            Usuń
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-neutral-400 italic">
                  Brak features dla tego produktu.
                </p>
              )}
            </div>

            {/* Pricing: LEAF -> preview, PACKAGE -> configurator */}
            {isPackage ? (
              <PackagePricingConfigurator
                productId={productId}
                brandId={brandId}
                packageStructure={packageStructure}
              />
            ) : (
              <ProductPricingPreview
                productId={productId}
                brandId={brandId}
                productType={product.productType}
                pricingComponentId={product.pricingComponentId}
              />
            )}

            {/* Package structure (C1, C2, C4) */}
            {isPackage && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-semibold text-neutral-900">
                    Struktura pakietu
                  </h4>
                  {canEditStructure && !editingStructure && (
                    <button
                      onClick={() => setEditingStructure(true)}
                      className="px-3 py-1.5 text-xs border border-primary-300 rounded-md hover:bg-primary-50 text-primary-700 font-medium"
                    >
                      Edytuj strukturę
                    </button>
                  )}
                </div>

                {editingStructure ? (
                  <PackageStructureEditor
                    productId={productId}
                    brandId={brandId}
                    initialStructure={packageStructure}
                    onSaved={() => {
                      setEditingStructure(false);
                      refetch();
                    }}
                    onCancel={() => setEditingStructure(false)}
                  />
                ) : packageStructure ? (
                  <PackageStructureViewer
                    packageStructure={packageStructure}
                  />
                ) : (
                  <p className="text-sm text-neutral-400 italic">
                    Brak struktury pakietu.
                    {canEditStructure && " Kliknij \"Edytuj strukturę\" aby dodać."}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Remove Feature Confirm Modal — rendered at z-[60] above the detail modal */}
      {removingFeatureId && (
        <RemoveFeatureConfirmModal
          isPending={removeMutation.isPending}
          onConfirm={handleRemoveFeatureConfirm}
          onClose={() => setRemovingFeatureId(null)}
        />
      )}
    </>
  );
};
